import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form } from 'coreOld/components';
import { DROPDOWN_STATE_BY_COUNTRY } from 'coreOld/constants';
import { InputType, Size } from 'coreOld/enums';
import { addressesService } from 'coreOld/services';
import { IAddress } from 'coreOld/types';
import { getRequiredLabel } from 'coreOld/utils';

import { FieldGroup } from './addressFormStyle';
import { addressSchema } from './addressSchema';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  submitBtnLabel: string;
  defaultValues?: IAddress;
  onSubmit: () => (data: IAddress) => void;
  disabledSubmit?: boolean;
  schema?;
  beforeSubmitContent?;
  footerContent?;
  hasGoBack?: boolean;
  goBack?: () => void;
  size?: Size;
  minWidth?: number;
}

const MIN_WIDTH = 362;

export const AddressFormView = ({
  isOpen,
  onClose,
  title,
  submitBtnLabel,
  defaultValues,
  onSubmit,
  disabledSubmit = false,
  schema,
  beforeSubmitContent,
  footerContent,
  hasGoBack = false,
  goBack,
  size,
  minWidth = MIN_WIDTH,
}: IProps) => {
  const { t } = useTranslation();

  const [country, setCountry] = useState(null);

  const isStateDropdown = DROPDOWN_STATE_BY_COUNTRY.includes(country?.abbreviation);

  const formSchema = schema || addressSchema(isStateDropdown);

  const StateField = isStateDropdown ? (
    <Field
      name="state"
      label={getRequiredLabel(t('labelState'))}
      type={InputType.ASYNC_SELECT}
      inputProps={{
        key: country?.abbreviation,
        getOptions: (filters) =>
          addressesService.getStatesAutocomplete({
            ...filters,
            filters: {
              country_abbreviation: country,
            },
          }),
        getLabel: (opt) => opt?.name,
        getValue: (opt) => opt,
        isClearable: false,
        showDropdownIcon: true,
        size: Size.XS,
      }}
    />
  ) : (
    <Field
      name="state"
      label={getRequiredLabel(t('labelState'))}
      type={InputType.TEXTFIELD}
      inputProps={{
        size: Size.LG,
      }}
    />
  );

  return (
    <Form<IAddress>
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      submitBtnLabel={submitBtnLabel}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      disabledSubmit={disabledSubmit}
      schema={formSchema}
      beforeSubmitContent={beforeSubmitContent}
      footerContent={footerContent}
      hasGoBack={hasGoBack}
      goBack={goBack}
      size={size}
      minWidth={minWidth}
    >
      <Field
        name="line1"
        label={getRequiredLabel(t('labelAddressLine1'))}
        type={InputType.TEXTFIELD}
        inputProps={{
          size: Size.LG,
          autoFocus: true,
        }}
      />
      <Field
        name="line2"
        label={t('labelAddressLine2')}
        type={InputType.TEXTFIELD}
        inputProps={{
          size: Size.LG,
        }}
      />
      <FieldGroup name="countryStateGroup">
        <Field
          name="country"
          label={getRequiredLabel(t('labelCountry'))}
          type={InputType.ASYNC_SELECT}
          inputProps={{
            getOptions: addressesService.getCountriesAutocomplete,
            getLabel: (opt) => opt?.name,
            getValue: (opt) => opt,
            isClearable: false,
            showDropdownIcon: true,
            size: Size.XS,
            onChange: (value) => setCountry(value),
          }}
        />
        {StateField}
      </FieldGroup>
      <FieldGroup name="postalCodeCityGroup">
        <Field
          name="postalCode"
          label={getRequiredLabel(t('labelPostalCode'))}
          type={InputType.TEXTFIELD}
          inputProps={{
            size: Size.XS,
          }}
        />
        <Field
          name="city"
          label={getRequiredLabel(t('labelCity'))}
          type={InputType.TEXTFIELD}
          inputProps={{
            size: Size.XS,
          }}
        />
      </FieldGroup>
      <Field
        name="locationName"
        label={getRequiredLabel(t('labelLocationName'))}
        type={InputType.TEXTFIELD}
        inputProps={{
          size: Size.LG,
        }}
      />
    </Form>
  );
};

export default AddressFormView;
